import { agentTrpcContract } from "@amtana/agent-contract";
import type {
  Agent,
  AgentIdInput,
  AgentOffers,
  CreateAgentInput,
  ListAgentsOutput,
  Ok,
  RenameAgentInput,
} from "@amtana/agent-contract";
import { useContractMutation, useContractQuery, useInvalidateContract } from "@amtana/browser";
import type { MutationResult, QueryResult } from "@amtana/browser";

// The caller's own agents (ADR-023 §1, §12): the list, one agent, the harnesses a new one may run
// on, the member's machine, and the writes that change the list. Every door answers not found
// while `remote-agents` is off, so a screen behind the flag never reads them.

/** How often the list and an agent's page ask again, so a status chip stays live. */
const LIVE_EVERY_MS = 5_000;
/** How often an agent's page asks while the agent wakes or works. */
const BUSY_EVERY_MS = 1_500;

export function useAgents(): QueryResult<ListAgentsOutput> {
  return useContractQuery(agentTrpcContract, "list", { input: {}, refetchInterval: LIVE_EVERY_MS });
}

export function useAgent(id: string, options: { readonly live?: boolean } = {}): QueryResult<Agent> {
  return useContractQuery(agentTrpcContract, "get", {
    input: { id },
    refetchInterval: options.live ? BUSY_EVERY_MS : LIVE_EVERY_MS,
  });
}

export function useAgentOffers(): QueryResult<AgentOffers> {
  return useContractQuery(agentTrpcContract, "offers", { input: {} });
}

type WriteName = "create" | "rename" | "delete";

function useWrite<TInput, TOutput>(name: WriteName): MutationResult<TOutput, TInput> {
  const mutation = useContractMutation(agentTrpcContract, name);
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<TOutput, TInput>;
  return {
    ...typed,
    run: (input: TInput) => typed.mutateAsync(input).finally(() => void invalidate(agentTrpcContract)),
  };
}

export const useCreateAgent = (): MutationResult<Agent, CreateAgentInput> => useWrite("create");
export const useRenameAgent = (): MutationResult<Agent, RenameAgentInput> => useWrite("rename");
export const useDeleteAgent = (): MutationResult<Ok, AgentIdInput> => useWrite("delete");

/** The Agents area's own path; the only place it is written. */
export const AGENTS_PATH = "/agents";

/** One agent's page. */
export function agentPath(id: string): string {
  return `${AGENTS_PATH}/${encodeURIComponent(id)}`;
}
